import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { fadeUp, viewport } from "@/lib/motion";
import { Search, Layers, UserCog, Sprout, Repeat } from "lucide-react";

const CHAPTERS = [
  {
    n: "01",
    title: "Assess",
    icon: Search,
    body: "We begin with a complete, quantified picture of your liver — using validated markers, imaging and clinical history rather than guesswork.",
  },
  {
    n: "02",
    title: "Stratify",
    icon: Layers,
    body: "Your data is translated into a precise Liver Risk Score, placing you on a clear, color-coded spectrum from low to critical risk.",
  },
  {
    n: "03",
    title: "Personalize",
    icon: UserCog,
    body: "No generic advice. A physician designs an intervention calibrated to your biology, lifestyle, and the specific drivers of your risk.",
  },
  {
    n: "04",
    title: "Restore",
    icon: Sprout,
    body: "A structured 12-week protocol targets liver fat, inflammation and metabolic health — with measurable checkpoints along the way.",
  },
  {
    n: "05",
    title: "Sustain",
    icon: Repeat,
    body: "We build durable habits and a monitoring cadence so your results hold — turning a recovery into a lifelong baseline.",
  },
];

export const Philosophy = () => {
  return (
    <section id="philosophy" className="relative bg-sa-forest py-16 md:py-20" data-testid="philosophy-section">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
            The SuperAge philosophy
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-white sm:text-5xl">
            Five principles of precision organ medicine.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-white/60">
            A single, deliberate framework guides every patient journey — from
            first click to lasting recovery.
          </p>
        </Reveal>

        <div className="mt-16 divide-y divide-white/10 border-y border-white/10">
          {CHAPTERS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.n}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
                className="group relative grid grid-cols-1 items-center gap-6 py-10 md:grid-cols-12"
              >
                <span className="pointer-events-none absolute -left-2 -top-2 select-none font-display text-[7rem] font-bold leading-none text-white/[0.03] md:text-[9rem]">
                  {c.n}
                </span>
                <div className="relative md:col-span-1">
                  <span className="font-serif text-2xl italic text-sa-copper">{c.n}</span>
                </div>
                <div className="relative flex items-center gap-4 md:col-span-4">
                  <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl border border-white/15 bg-white/5 text-sa-copper transition-colors duration-300 group-hover:border-sa-copper/40">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-3xl font-medium tracking-tight text-white md:text-4xl">
                    {c.title}
                  </h3>
                </div>
                <p className="relative text-white/60 md:col-span-7 md:text-lg">
                  {c.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Philosophy;
